import { Icon as BlueIcon, IconName } from '@blueprintjs/core';
import {
  ActionIcon as MantineActionIcon,
  ActionIconProps,
  createPolymorphicComponent,
  Sx,
} from '@mantine/core';
import { getHoverColor, iconsNormalIndex } from '@services/mantine';
import COLORS_FLAT from '@styles/colors/themes/beam/beam-flat';
import { forwardRef } from 'react';

interface BActionIconProps extends ActionIconProps {
  icon?: IconName;
}
const sx: Sx = theme => {
  return {
    [`&[data-variant="subtle"]`]: {
      ...theme.fn.hover({
        backgroundColor: getHoverColor(),
      }),
      '&:disabled': {
        ...theme.fn.hover({
          backgroundColor: COLORS_FLAT.n200,
        }),
      },
    },
    [`&[data-variant="outline"]`]: {
      borderColor: COLORS_FLAT.n400,
      ...theme.fn.hover({
        backgroundColor: '#fff',
      }),
    },
    '&&': {
      transitionDuration: 'var(--transition-duration)',
      transitionProperty: 'background-color',
      transitionTimingFunction: 'var(--transition-fn)',
    },
    '&:active': {
      transform: 'unset',
    },
    '&:disabled': {
      backgroundColor: COLORS_FLAT.n200,
      color: COLORS_FLAT.n500,
    },
  };
};

/**
 * Will render action icon or children by default for custom icons.
 * Use data-varaint to change styles per variant. See: https://mantine.dev/changelog/5-0-0/
 * Todo: Move to default props.
 */
function _BActionIcon(
  props: BActionIconProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { children, icon = 'cross', ...rest } = props;
  return (
    <MantineActionIcon
      size={32}
      sx={sx}
      color={iconsNormalIndex}
      radius="md"
      {...rest}
      ref={ref}
      data-enable-new-icon
      // Assuming subtle is the default prop
      data-variant={rest.variant ?? 'subtle'}
    >
      {children ?? <BlueIcon icon={icon} />}
    </MantineActionIcon>
  );
}

const FWBActionIcon = forwardRef(_BActionIcon);
export const BActionIcon = createPolymorphicComponent<
  'button',
  BActionIconProps
>(FWBActionIcon);
